import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Clock, MapPin, Star, ShieldCheck, Sparkles, Home, Hammer } from "lucide-react";
import { Logo } from "@/components/Logo";
import SocialProofToaster from "@/components/SocialProofToaster";

const HERO_IMG = "/images/screens/screens-tree.jpg";
const SERVICE_IMG_1 = "/images/screens/screens-driveway.jpg";
const SERVICE_IMG_2 = "/images/screens/screens-porch.jpg";
const SERVICE_IMG_3 = "/images/screens/screens-brick.jpg";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      {/* Nav */}
      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={36} withWordmark />
        </Link>
        <div className="flex items-center gap-2 sm:gap-5">
          <Link
            to="/signup"
            data-testid="nav-pro-link"
            className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-slate-900"
          >
            For screen companies
          </Link>
          <Link to="/quote" data-testid="nav-quote-cta">
            <Button className="bg-[#0F172A] hover:bg-black rounded-full h-10 px-5 text-sm font-semibold">
              Get my quote
            </Button>
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 space-y-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100/70 border border-amber-200 text-xs font-semibold text-amber-800">
              <Clock className="w-3.5 h-3.5" /> Pricing in under 60 seconds
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.03]">
              Need new window screens?
              <span className="block mt-2 text-[#C75D33]">Get an instant quote.</span>
            </h1>

            <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
              New screens, rescreens, and sliding screen doors. Answer a few quick questions and see your price right away — straight from a local screen pro.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <Link to="/quote" data-testid="hero-quote-btn" className="block">
                <Button size="lg" className="w-full sm:w-auto bg-[#0F172A] hover:bg-black rounded-2xl h-14 px-8 text-base font-bold shadow-lg shadow-[#C75D33]/20">
                  Get My Quote <ArrowRight className="w-5 h-5 ml-1.5" />
                </Button>
              </Link>
              <div className="flex items-center gap-1.5 text-sm text-slate-500 sm:pl-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Free · No obligation
              </div>
            </div>

            <ul className="flex flex-wrap gap-2 pt-2">
              <Trust icon={Clock} text="60-second quote" />
              <Trust icon={ShieldCheck} text="Real local pricing" />
              <Trust icon={MapPin} text="Local screen pros" />
              <Trust icon={CheckCircle2} text="Free for homeowners" />
            </ul>

            <div className="pt-2">
              <Link
                to="/signup"
                data-testid="hero-pro-link"
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#C75D33] font-medium border-b border-dashed border-slate-300 hover:border-[#C75D33] pb-0.5 transition-colors"
              >
                Own a screen company? Give instant quotes <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-slate-900/15 aspect-[4/5] sm:aspect-[5/6] bg-slate-200">
              <img
                src={HERO_IMG}
                alt="Newly built window screens ready for install at a customer's home"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {/* Soft gradient for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent"></div>

              {/* Bottom badge — real, grounded, single stat */}
              <div className="absolute left-5 right-5 bottom-5 sm:left-7 sm:right-auto sm:bottom-7 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 max-w-xs">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-display font-bold text-slate-900 text-sm leading-tight">Avg. quote time</div>
                  <div className="text-xs text-slate-600">Under 60 seconds</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-slate-200/70 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-6">
          <Stat icon={Clock} value="60 sec" label="Avg. quote time" />
          <Stat icon={ShieldCheck} value="$0" label="To get pricing" />
          <Stat icon={MapPin} value="Local" label="Real screen pros" />
          <Stat icon={Star} value="5-star" label="Friendly local service" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-14">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Three steps. Sixty seconds. Done.
            </h2>
            <p className="text-slate-600 mt-4 text-base sm:text-lg">No tech know-how needed. Done in about a minute.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Step n="1" title="Tell us what you need" desc="New screens, rescreens, or sliding screen doors — pick what fits." />
            <Step n="2" title="See your price right away" desc="Real numbers on the screen — based on real local pricing." />
            <Step n="3" title="A local pro reaches out" desc="Your local screen company follows up to confirm details and schedule the work." />
          </div>
          <div className="text-center mt-12">
            <Link to="/quote" data-testid="how-it-works-cta">
              <Button size="lg" className="bg-[#0F172A] hover:bg-black rounded-2xl h-14 px-8 text-base font-bold shadow-lg shadow-[#C75D33]/20">
                Get My Quote <ArrowRight className="w-5 h-5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 lg:py-20 bg-white border-y border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">What we quote</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Anything to do with window screens.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ServiceCard img={SERVICE_IMG_2} title="New window screens" desc="Brand new screens built and installed for your windows." />
            <ServiceCard img={SERVICE_IMG_1} title="Rescreens" desc="Replace torn, bent, or sun-damaged mesh — keep the frame." />
            <ServiceCard img={SERVICE_IMG_3} title="Sliding screen doors" desc="New patio screen doors or rescreens for the ones you have." />
          </div>
        </div>
      </section>

      {/* WHY WINDOWSCREENQUOTE */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-12 lg:mb-14">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Why WindowScreenQuote</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              The easiest way to get screens done.
            </h2>
            <p className="text-slate-600 mt-4 text-base sm:text-lg">
              Buying screens shouldn't feel like a guessing game. Get a real price in real time, then a local screen pro takes care of the rest.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Why
              icon={ShieldCheck}
              title="Honest, upfront pricing"
              desc="See real numbers based on real local pricing — not 'call for a quote.'"
            />
            <Why
              icon={MapPin}
              title="Local screen pros"
              desc="Your quote routes to a vetted local screen company in your area."
            />
            <Why
              icon={Hammer}
              title="Done by a real pro"
              desc="Once you're ready, a local company handles measuring, building, and install."
            />
            <Why
              icon={Home}
              title="Free for homeowners"
              desc="Always free to get pricing. No fees, no upsells, no fine print."
            />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / TESTIMONIAL */}
      <section className="py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <div className="flex justify-center gap-0.5 text-amber-400 mb-5">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-5 h-5 fill-current" />)}
          </div>
          <p className="font-display text-2xl sm:text-3xl text-slate-900 leading-relaxed">
            “I had pricing in less than a minute. A local company came out the next week and replaced all six screens. Zero hassle.”
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 text-sm text-slate-600">
            <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 font-display font-bold flex items-center justify-center">M</div>
            <div className="text-left">
              <div className="font-semibold text-slate-900">Maria T.</div>
              <div className="text-xs text-slate-500">Homeowner · Phoenix, AZ</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA — HOMEOWNER */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-[32px] bg-slate-900 px-8 py-14 sm:px-12 sm:py-20 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C75D33]/30 blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl"></div>
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                See your screen quote now.
              </h2>
              <p className="text-slate-300 mt-4 text-base sm:text-lg max-w-xl mx-auto">
                Free. Instant. Real local pricing — straight from a local screen pro.
              </p>
              <Link to="/quote" className="inline-block mt-7" data-testid="final-cta-btn">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 rounded-2xl h-14 px-8 text-base font-bold">
                  Get My Quote <ArrowRight className="w-5 h-5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECONDARY: FOR SCREEN COMPANIES */}
      <section id="for-companies" className="pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-[28px] bg-white border border-slate-200 p-7 sm:p-10 shadow-sm grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-[11px] font-bold uppercase tracking-wider text-amber-800">
                <Sparkles className="w-3.5 h-3.5" /> For screen companies
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-4 tracking-tight">
                Own a screen company?<br className="hidden sm:block" /> Get more leads on autopilot.
              </h2>
              <p className="text-slate-600 mt-4 text-base leading-relaxed max-w-xl">
                Your own white-label instant-quote page. Homeowners get a real price, you get a hot lead with the job already spec'd out.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5 mt-6 max-w-lg">
                <Pro text="Your branding, your pricing" />
                <Pro text="Lead dashboard + Kanban" />
                <Pro text="Email notifications" />
                <Pro text="Capture mode controls" />
              </ul>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <Link to="/signup" data-testid="pro-signup-cta" className="w-full lg:w-auto">
                <Button size="lg" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-12 px-6 text-sm font-bold">
                  Start $1 trial <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/login" data-testid="pro-login-link" className="text-sm font-semibold text-slate-600 hover:text-slate-900">
                Pro login
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-14 grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-1 sm:pr-4">
            <Link to="/" className="flex items-center gap-2.5">
              <Logo size={36} withWordmark />
            </Link>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">Instant window screen quotes — real local pricing, real local pros.</p>
            <Link to="/quote" className="inline-block mt-4" data-testid="footer-cta">
              <Button className="bg-[#0F172A] hover:bg-black rounded-full h-10 px-5 text-sm font-semibold">
                Get my quote
              </Button>
            </Link>
          </div>

          {/* Services */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Services</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/window-screen-repair" className="hover:text-[#C75D33]" data-testid="footer-svc-screen-repair">Window Screen Repair</Link></li>
              <li><Link to="/new-window-screens" className="hover:text-[#C75D33]" data-testid="footer-svc-new-screens">New Window Screens</Link></li>
              <li><Link to="/window-rescreening" className="hover:text-[#C75D33]" data-testid="footer-svc-rescreening">Window Rescreening</Link></li>
              <li><Link to="/sliding-screen-door-repair" className="hover:text-[#C75D33]" data-testid="footer-svc-sliding-repair">Sliding Screen Door Repair</Link></li>
              <li><Link to="/retractable-screen-doors" className="hover:text-[#C75D33]" data-testid="footer-svc-retractable">Retractable Screen Doors</Link></li>
              <li><Link to="/security-screen-doors" className="hover:text-[#C75D33]" data-testid="footer-svc-security">Security Screen Doors</Link></li>
            </ul>
          </div>

          {/* Popular cities */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Popular cities</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/arizona/phoenix/window-screen-repair" className="hover:text-[#C75D33]">Phoenix, AZ</Link></li>
              <li><Link to="/texas/dallas/window-screen-repair" className="hover:text-[#C75D33]">Dallas, TX</Link></li>
              <li><Link to="/florida/tampa/window-screen-repair" className="hover:text-[#C75D33]">Tampa, FL</Link></li>
              <li><Link to="/california/murrieta/window-screen-repair" className="hover:text-[#C75D33]">Murrieta, CA</Link></li>
              <li><Link to="/nevada/las-vegas/window-screen-repair" className="hover:text-[#C75D33]">Las Vegas, NV</Link></li>
              <li><Link to="/georgia/atlanta/window-screen-repair" className="hover:text-[#C75D33]">Atlanta, GA</Link></li>
              <li><Link to="/north-carolina/charlotte/window-screen-repair" className="hover:text-[#C75D33]">Charlotte, NC</Link></li>
              <li><Link to="/tennessee/nashville/window-screen-repair" className="hover:text-[#C75D33]">Nashville, TN</Link></li>
            </ul>
            <Link to="/locations" className="inline-flex items-center gap-1 text-xs text-[#C75D33] font-semibold mt-3 hover:underline" data-testid="footer-all-locations">
              View all locations <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Company */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Company</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/services" className="hover:text-[#C75D33]">All services</Link></li>
              <li><Link to="/locations" className="hover:text-[#C75D33]">All locations</Link></li>
              <li><Link to="/contact" className="hover:text-[#C75D33]" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/terms" className="hover:text-[#C75D33]" data-testid="footer-terms">Terms</Link></li>
              <li><Link to="/privacy" className="hover:text-[#C75D33]" data-testid="footer-privacy">Privacy</Link></li>
              <li><Link to="/login" className="hover:text-[#C75D33]">Pro login</Link></li>
              <li><Link to="/for-screen-companies" className="hover:text-[#C75D33]">For screen companies</Link></li>
            </ul>
            <div className="mt-4 text-xs text-slate-500 leading-relaxed">
              <a href="mailto:support@windowscreenquote.com" className="hover:text-[#C75D33]" data-testid="footer-support-email">support@windowscreenquote.com</a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200/70">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col gap-2 text-xs text-slate-500">
            <div className="flex flex-col sm:flex-row justify-between gap-2">
              <span>© WindowScreenQuote.com {new Date().getFullYear()}</span>
              <span>Real local pricing from real local screen pros.</span>
            </div>
            <div className="text-slate-400 leading-relaxed pt-1">
              <strong>SMS notice:</strong> By providing your mobile number on our forms, you agree to receive transactional SMS from WindowScreenQuote.com about your quote, appointments, customer service, or account notifications. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, Reply HELP for help. SMS consent is not shared with third parties or affiliates for marketing purposes. See <Link to="/privacy" className="underline hover:text-slate-600">Privacy</Link> &amp; <Link to="/terms" className="underline hover:text-slate-600">Terms</Link>.
            </div>
          </div>
        </div>
      </footer>
      <SocialProofToaster />
    </div>
  );
}

function Trust({ icon: Icon, text }) {
  return (
    <li className="inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-medium text-slate-700">
      <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
      {text}
    </li>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-slate-900 leading-tight">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="bg-white rounded-3xl p-7 border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <div className="w-12 h-12 rounded-2xl bg-[#0F172A] text-white flex items-center justify-center font-display font-bold text-xl mb-4">{n}</div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function ServiceCard({ img, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-slate-900 text-lg">{title}</h3>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Why({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
      <div className="w-11 h-11 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-display font-bold text-slate-900 text-base mb-1.5">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function Pro({ text }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-700">
      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {text}
    </li>
  );
}
